<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import Logo from './../../../components/logo'
import Donate from './tabs/donate.vue'
import ViewPlans from './tabs/view-plans.vue'
import { useAnalytics } from '@composables/use-analytics'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import {
    WALL_TYPES,
    PRESENTATION_TYPES,
    TRIGGER_REASONS,
    ENTRY_POINTS,
    CTA_TYPES,
    DISMISS_METHODS
} from '@services/mixpanel/analytics-spec'
import postAuthRedirect from '@utils/post-auth-redirect'
import premiumFunnelOrigin from '@utils/premium-funnel-origin'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { ViewPlans, Donate }

const BANNER_ENTRIES = ['home-banner', 'radio-banner']

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'ViewPlans',
    },
})

const route = useRoute()
const router = useRouter()
const store = useStore()
const analyticsService = useAnalytics()

const selectedDonationAmount = ref(null)

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const isLoggedIn = computed(() => store.getters['auth/loggedIn'])

const plans = computed(() => store.getters['premium/sortedPremiumPlans'])

const currentSubscription = computed(() => store.getters['premium/activeSubscription'])

const isPremiumUser = computed(() => store.getters['premium/isPremium'])

const entitledFreeTrial = computed(() => !store.getters['premium/hasEverBeenPremium'])

const filteredPlans = computed(() => plans.value.filter(plan => plan.active === 1))

const tabs = computed(() => [
    { to: `/premium/view-plans`, label: 'Premium' },
    { to: `/premium/donate`, label: 'Donate' },
])

function isUpsellBannerEntry() {
    const from = route.query.from || ''
    return typeof from === 'string' &&
        (BANNER_ENTRIES.includes(from) || from.startsWith('radio-channel-'))
}

function getEntryPoint() {
    const referrer = document.referrer || ''
    const query = route.query
    if (query.from === 'topbar') return ENTRY_POINTS.TOP_BAR
    if (query.from === 'sidebar') return ENTRY_POINTS.SIDEBAR
    if (query.from === 'modal') return ENTRY_POINTS.PREMIUM_UPSELL_MODAL
    if (query.from === 'settings-external-players') return ENTRY_POINTS.SETTINGS_EXTERNAL_PLAYERS
    if (query.from === 'settings') return ENTRY_POINTS.SETTINGS_AUDIO_QUALITY
    if (query.from === 'account') return ENTRY_POINTS.ACCOUNT_MENU
    if (query.from === 'home-banner') return ENTRY_POINTS.HOME_BANNER
    if (query.from === 'radio-banner') return ENTRY_POINTS.RADIO_BANNER
    if (typeof query.from === 'string' && query.from.startsWith('radio-channel-')) return ENTRY_POINTS.RADIO_CHANNEL_BANNER
    // Must precede the generic '/settings' check below, which would otherwise
    // swallow this path and miscredit it as audio-quality. Tracks the path of
    // the 'settings/external-player' route — keep the two in step if it moves.
    if (referrer.includes('/settings/external-player')) return ENTRY_POINTS.SETTINGS_EXTERNAL_PLAYERS
    if (referrer.includes('/settings')) return ENTRY_POINTS.SETTINGS_AUDIO_QUALITY
    if (referrer.includes('/account')) return ENTRY_POINTS.ACCOUNT_MENU
    return ENTRY_POINTS.DIRECT_NAVIGATION
}

function selectPlan(plan) {
    paywallTracker.onCtaTapped(CTA_TYPES.VIEW_PLANS)
    analyticsService.premiumUpsellPlanSelected(plan.id)
    const prefix = route.path.includes('offer') ? '/offer' : ''
    const from = typeof route.query.from === 'string' && route.query.from
        ? `?from=${encodeURIComponent(route.query.from)}`
        : ''
    const target = `${prefix}/premium/select-payment/${plan.id}${from}`
    if (!isLoggedIn.value) {
        // Auth sits between plan pick and payment: stash the payment
        // page (plan id in the path, source for attribution, postAuth
        // marker for the already-Premium dissolve) and default to
        // sign-up — most signed-out pickers are new visitors.
        postAuthRedirect.set(`${target}${from ? '&' : '?'}postAuth=1`)
        router.push({ name: 'signup' })
        return
    }
    router.push({ path: target })
}

function selectAmount(amount) {
    selectedDonationAmount.value = amount
}

function handleDismiss() {
    paywallTracker.onWallDismissed(DISMISS_METHODS.CLOSE)
    router.push(premiumFunnelOrigin.consume() || { name: 'home' })
}

// A logged-out visitor who taps the upsell banner is routed here after
// auth (via the post-auth redirect). If they turn out to be an existing
// Premium subscriber, the upsell is moot — send them Home instead of the
// plans page. Premium status resolves after login, so react to it here
// rather than at redirect time. Scoped to banner entries so a Premium
// member who opens the plans page deliberately still sees their plan.
watch(isPremiumUser, isPremium => {
    if (isPremium && isUpsellBannerEntry()) {
        router.replace(premiumFunnelOrigin.consume() || { name: 'home' })
    }
}, { immediate: true })

onMounted(() => {
    paywallTracker.onWallShown({
        wallType: WALL_TYPES.VIEW_PLANS,
        presentation: PRESENTATION_TYPES.PAGE,
        triggerReason: TRIGGER_REASONS.USER_INITIATED,
        entryPoint: getEntryPoint(),
    })
})
</script>

<template>
    <div>
        <BaseContent v-if="tabs.length">
            <BaseSection padded>
                <Logo
                    :is-linked-to-home="false"
                    size="m"
                />
            </BaseSection>
            <BaseTabPanel
                :tabs="tabs"
                is-unpadded
                address-navigation
            />
            <BaseContent>
                <component
                    :is="mainView"
                    :plans="filteredPlans"
                    :current-subscription="currentSubscription"
                    :is-logged-in="isLoggedIn"
                    :is-entitled-free-trial="entitledFreeTrial"
                    @selectAmount="selectAmount"
                    @onAction="selectPlan"
                />
            </BaseContent>
            <BaseSection padded>
                <div
                    class="escape-url"
                    @click="handleDismiss"
                >No, thanks. I like ads and low quality audio.</div>
            </BaseSection>
        </BaseContent>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.escape-url {
    @extend %contentSubtitleM;

    margin-top: $s-xxl;
    text-align: center;
    text-decoration: underline;
    cursor: pointer;
}

@include tablet {
    .escape-url {
        text-align: left;
    }
}
</style>
